import { useEffect, useMemo, useRef, useState } from "react";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-datepicker/dist/react-datepicker.css";
import { BeatLoader } from "react-spinners";
import CustomHeader from "../../components/CustomHeader/CustomHeader";
import { useUserState } from "../../context/UserContext";
import { useAuthentication } from "../hooks/useAuthentication";
import { fetchCityData, fetchGlobalData } from "./api/fetchData";
import "./App.css";
import RoomCalendar from "./components/Calendars/RoomCalendar";
import SchoolCalendar from "./components/Calendars/SchoolCalendar";
import {
  handleCityChange,
  handleDateChange,
  handleDateClick,
  handleDefaultCityChange,
  handleEventClick,
  handleRoomChange,
  handleSubmitDefaultCity,
} from "./components/handlers";
import CreateModal from "./components/Modals/CreateModal";
import InfoModal from "./components/Modals/InfoModal";
import BusyEvents from "./components/OccupiedEvents/BusyEvents";
import { adjustDate, getTitle, getUsersName, selectStyles } from "./utils";

const CalendarIpd = () => {
  const currentDate = new Date();
  const { role, userData, setUserData } = useUserState();
  const [startDateSecond, setStartDateSecond] = useState(
    new Date().toISOString().split("T")[0]
  );
  const calendarRef = useRef(null);
  const { authToken } = useAuthentication();
  const [events, setEvents] = useState();
  const [isDayGridMonth, setIsDayGridMonth] = useState(false);
  const [info, setInfo] = useState({});
  const [rooms, setRooms] = useState([]);
  const [cities, setCities] = useState([]);
  const [secondRooms, setSecondRooms] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [type, setType] = useState("");
  const [zakupitelj, setZakupitelj] = useState([]);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [zakupiteljType, setZakupiteljType] = useState([]);
  const [isBusyOpen, setIsBusyOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [busyAppointmentInfo, setBusyAppointmentInfo] = useState(false);
  const [defaultSelectedCity, setDefaultSelectedCity] = useState(
    userData?.defaultCity || null
  );
  const [selectedCity, setSelectedCity] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [startDate, setStartDate] = useState(
    new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
  );
  const [endDate, setEndDate] = useState(
    new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
  );
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [showDefaultCityRadios, setShowDefaultCityRadios] = useState(
    userData?.defaultCity === null
  );
  const licence = document.querySelector(".fc-license-message");
  if (licence) licence.style.display = "none";
  const [rawEvents, setRawEvents] = useState([]);
  const [secondSelectedRoomId, setSecondSelectedRoomId] = useState(null);
  const [citiesForSecond, setCitiesForSecond] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [reRender, setReRender] = useState(false);
  const [editEventValues, setEditEventValues] = useState({
    eventId: "",
    eventType: "",
    supervisionType: "",
    clientId: "",
    creatorId: "",
    supervisorId: "",
    outsideClient: "",
    name: "",
    date: "",
    timeFrom: "",
    timeTo: "",
    description: "",
    roomId: "",
  });

  useEffect(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().gotoDate(startDateSecond);
    }
  }, [startDateSecond]);

  useEffect(() => {
    if (!authToken || !userData?.id) return;
    fetchGlobalData({
      authToken,
      userData,
      setAllUsers,
      setCities,
      setCitiesForSecond,
      setZakupitelj,
      setSelectedCity,
      setAllRooms,
    });
  }, [authToken, userData]);

  useEffect(() => {
    if (!authToken || !userData?.id || !selectedCity?.value) return;
    fetchCityData({
      authToken,
      selectedCity,
      setRooms,
      setSecondRooms,
      startDate,
      setRawEvents,
      setEvents,
      allRooms,
    });
  }, [authToken, userData?.id, selectedCity, startDate]);

  useEffect(() => {
    if (!rawEvents.length) return;

    const filtered =
      selectedRooms.length > 0
        ? rawEvents.filter((event) => selectedRooms.includes(event.roomId))
        : rawEvents;

    setEvents(filtered);
  }, [selectedRooms, rawEvents]);

  const transformedAllEventsSchoolCalendar = useMemo(() => {
    return events?.map((event) => {
      const startDate = new Date(event.start);
      const endDate = new Date(event.end);
      const changeDate = new Date("2025-03-30T00:00:00");

      if (startDate > changeDate) {
        startDate.setHours(startDate.getHours() - 2);
        endDate.setHours(endDate.getHours() - 2);
      } else {
        startDate.setHours(startDate.getHours() - 1);
        endDate.setHours(endDate.getHours() - 1);
      }

      return {
        ...event,
        userId: event.therapistId,
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      };
    });
  }, [events]);

  const transformedEventsRoomCalendar = useMemo(() => {
    return rawEvents?.map((event) => {
      const start = event.start ? new Date(event.start) : null;
      const end = event.end ? new Date(event.end) : null;
      const changeDate = new Date("2025-03-30T00:00:00");

      return {
        id: `${event.id}-${event.eventType}`,
        userId: event.therapistId || event.supervisorId || event.creatorId,
        title: getTitle(event, userData.id, allUsers),
        start: adjustDate(start, start, changeDate),
        end: adjustDate(end, start, changeDate),
        resourceId: event?.roomId?.toString() || null,
      };
    });
  }, [events]);

  return (
    <div>
      {(isLoading || loadingDelete) && (
        <div className={"loadingContainer"}>
          <BeatLoader color="#cb9c2d" />
        </div>
      )}
      {isCreateOpen && (
        <CreateModal
          type={type}
          users={allUsers}
          date={selectedDate}
          rooms={rooms}
          onClick={() => setIsCreateOpen(false)}
          startDate={startDate}
          endDate={endDate}
          selectedCity={selectedCity}
          selectedRooms={selectedRooms}
          setEvents={setEvents}
          setLoadingDelete={setLoadingDelete}
          reRender={reRender}
          setReRender={setReRender}
          setIsCreateOpen={setIsCreateOpen}
          editEventValues={editEventValues}
          secondSelectedRoomId={secondSelectedRoomId}
          zakupiteljType={zakupiteljType}
          setIsBusyOpen={setIsBusyOpen}
          setBusyAppointmentInfo={setBusyAppointmentInfo}
          getUsersName={getUsersName}
          setIsLoading={setIsLoading}
        />
      )}
      {isInfoOpen && (
        <InfoModal
          data={info}
          rooms={allRooms}
          onClick={() => setIsInfoOpen(false)}
          userData={userData}
          getUsersName={getUsersName}
        />
      )}
      <BusyEvents
        isBusyOpen={isBusyOpen}
        busyAppointmentInfo={busyAppointmentInfo}
        allUsers={allUsers}
        setIsBusyOpen={setIsBusyOpen}
      />

      <CustomHeader title={"Školski kalendar"} />
      <SchoolCalendar
        cityProps={{
          cities,
          selectedCity,
          defaultSelectedCity,
          showDefaultCityRadios,
          handleCityChange: (selectedOptions) =>
            handleCityChange(
              selectedOptions,
              setSelectedCity,
              setZakupiteljType,
              userData,
              zakupitelj,
              setSelectedRooms
            ),
          handleDefaultCityChange: (value, label) =>
            handleDefaultCityChange(setDefaultSelectedCity, value, label),
          handleSubmitDefaultCity: () =>
            handleSubmitDefaultCity(
              defaultSelectedCity,
              authToken,
              setShowDefaultCityRadios,
              setUserData
            ),
        }}
        roomProps={{
          rooms,
          selectedRooms,
          handleRoomChange: (selectedOptions) =>
            handleRoomChange(selectedOptions, setSelectedRooms),
        }}
        calendarControls={{
          handleDateClick: (info) =>
            handleDateClick(
              info,
              setType,
              setSelectedDate,
              setSecondSelectedRoomId,
              setIsCreateOpen
            ),
          handleEventClick: (info) =>
            handleEventClick({
              info,
              role,
              zakupiteljType,
              userData,
              setType,
              setInfo,
              setIsInfoOpen,
              setEditEventValues,
              setIsCreateOpen,
            }),
          setIsDayGridMonth,
          setEvents,
          setStartDate,
          setEndDate,
        }}
        stateData={{
          startDate,
          endDate,
          isDayGridMonth,
          role,
          zakupiteljType,
          authToken,
          transformedAllEventsSchoolCalendar,
          userData,
          allUsers,
        }}
      />

      {(zakupiteljType !== null ||
        role === "Referada" ||
        role === "Zakupitelj") && (
        <RoomCalendar
          cityProps={{
            citiesForSecond,
            handleCityChange: (selectedOptions) =>
              handleCityChange(
                selectedOptions,
                setSelectedCity,
                setZakupiteljType,
                userData,
                zakupitelj,
                setSelectedRooms
              ),
            selectedCity,
          }}
          roomProps={{ secondRooms, selectedRooms }}
          calendarControls={{
            handleDateChange: (event) =>
              handleDateChange(event, setStartDateSecond),
            setStartDateSecond,
            setEvents,
            setStartDate,
            setEndDate,
            handleDateClick: (info) =>
              handleDateClick(
                info,
                setType,
                setSelectedDate,
                setSecondSelectedRoomId,
                setIsCreateOpen
              ),
            handleEventClick: (info) =>
              handleEventClick({
                info,
                role,
                zakupiteljType,
                userData,
                setType,
                setInfo,
                setIsInfoOpen,
                setEditEventValues,
                setIsCreateOpen,
              }),
          }}
          stateData={{
            selectStyles,
            calendarRef,
            authToken,
            role,
            transformedEventsRoomCalendar,
            userData,
            startDateSecond,
            startDate,
            endDate,
          }}
        />
      )}
    </div>
  );
};

export default CalendarIpd;
